import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { string, bool, object, func } from "prop-types";

const makeFirstLetterCapital = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const Input = ({
  variant = "outlined",
  type = "text",
  name,
  data,
  label,
  required = true,
  error,
  onInputChange,
  breakPoints = { xl: 12 },
  // #109. This used to be hard-coded "off" on the TextField below, which locked
  // every consumer out of the browser/password-manager contract — a signup
  // password could not be offered a generated one. It is an explicit named prop
  // rather than a {...rest} spread on purpose: a spread would also leak `data`,
  // `breakPoints` and `onInputChange` onto the DOM. The default stays "off" so
  // every caller that does not opt in renders exactly as it did before.
  autoComplete = "off",
  // B-46. Names: the keyboard must not "correct" an unusual surname. Off by
  // default, so every other field renders exactly as before.
  noAutoCorrect = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const isDate = type === "date";
  const effectiveType = isPassword && showPassword ? "text" : type;

  return (
    <Grid item xs={12} {...breakPoints}>
      <TextField
        variant={variant}
        label={makeFirstLetterCapital(label)}
        type={effectiveType}
        id={name}
        name={name}
        value={data[name] ? data[name] : ""}
        required={required}
        helperText={error && error.message ? error.message : ""}
        error={Boolean(error)}
        onChange={onInputChange}
        fullWidth
        autoComplete={autoComplete}
        inputProps={noAutoCorrect ? { autoCorrect: "off", spellCheck: false } : undefined}
        aria-label={makeFirstLetterCapital(label)}
        aria-describedby={error ? `${name}-error` : undefined}
        aria-invalid={Boolean(error)}
        InputLabelProps={isDate ? { shrink: true } : undefined}
        InputProps={
          isPassword
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }
            : undefined
        }
      />
    </Grid>
  );
};
Input.propTypes = {
  name: string.isRequired,
  required: bool.isRequired,
  type: string.isRequired,
  error: object,
  onInputChange: func.isRequired,
  variant: string,
  data: object,
  autoComplete: string,
  noAutoCorrect: bool,
};
Input.defaultProps = {
  required: true,
  type: "text",
  variant: "outlined",
  autoComplete: "off",
};

export default React.memo(Input);
